import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Package, FileText, Settings, MessageSquare, LogOut, Menu, X, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const LOGO_FULL = "/logo.png";

const sidebarLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/products', icon: Package, label: 'Products' },
  { to: '/admin/blogs', icon: FileText, label: 'Blog Posts' },
  { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/admin/site-content', icon: Settings, label: 'Site Content' },
  { to: '/admin/change-password', icon: KeyRound, label: 'Change Password' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-card border-b border-border h-16 flex items-center px-4 md:px-6" data-testid="admin-header">
        <Button variant="ghost" size="icon" className="md:hidden mr-2" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-sidebar-toggle">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="logo-container h-8 px-2">
          <img src={LOGO_FULL} alt="Dr. Preet" className="h-6 object-contain" />
        </div>
        <span className="ml-3 text-sm font-medium text-muted-foreground">Admin Panel</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.email}</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`admin-sidebar w-64 bg-card border-r border-border p-4 fixed md:sticky top-16 z-40 transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} data-testid="admin-sidebar">
          <nav className="space-y-1">
            {sidebarLinks.map(link => (
              <NavLink key={link.to} to={link.to} end={link.end} onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                <link.icon className="w-4 h-4" /> {link.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* Main content */}
        <main className="flex-1 p-6 md:p-8 min-h-[calc(100vh-64px)]" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
